import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, IconButton, Tooltip, CircularProgress,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const LEVEL_STYLES = {
  info: { bg: "rgba(107,72,255,0.1)", color: "#6B48FF" },
  warn: { bg: "rgba(245,158,11,0.1)", color: "#F59E0B" },
  error: { bg: "rgba(239,68,68,0.1)", color: "#EF4444" },
  critical: { bg: "rgba(239,68,68,0.15)", color: "#EF4444" },
};

export default function LogsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const loadLogs = async () => {
    try {
      setLoading(true);
      const resp = await hubApi.getDashboard();
      setData(resp.data);
    } catch (err) {
      console.error("Logs error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  const alerts = data?.recentAlerts || [];
  const filtered = filter === "all" ? alerts : alerts.filter((a) => a.level === filter);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6B48FF" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>Logs & Auditoria</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Eventos das ultimas 24h
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <ToggleButtonGroup
            size="small"
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
          >
            <ToggleButton value="all" sx={{ color: "#9CA3AF", fontSize: 11, textTransform: "none" }}>Todos</ToggleButton>
            <ToggleButton value="warn" sx={{ color: "#F59E0B", fontSize: 11, textTransform: "none" }}>Warning</ToggleButton>
            <ToggleButton value="error" sx={{ color: "#EF4444", fontSize: 11, textTransform: "none" }}>Error</ToggleButton>
            <ToggleButton value="critical" sx={{ color: "#EF4444", fontSize: 11, textTransform: "none" }}>Critical</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadLogs} sx={{ color: "#6B7280" }}><Refresh /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {filtered.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "#6B7280" }}>Nenhum evento encontrado</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)", width: 90 }}>Nivel</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>Evento</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)", width: 300 }}>Detalhes</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)", width: 100 }}>Horario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((log, i) => {
                  const style = LEVEL_STYLES[log.level] || LEVEL_STYLES.info;
                  const details = log.details || {};
                  const detailStr = Object.entries(details)
                    .filter(([k]) => !["hubId", "hubName", "source"].includes(k))
                    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                    .join(", ");

                  return (
                    <TableRow key={i} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.02)" } }}>
                      <TableCell sx={{ borderColor: "rgba(255,255,255,0.06)" }}>
                        <Chip
                          size="small"
                          label={log.level}
                          sx={{ fontSize: 10, height: 20, bgcolor: style.bg, color: style.color }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#D1D5DB", fontSize: 12, borderColor: "rgba(255,255,255,0.06)", fontFamily: "monospace" }}>
                        {log.action}
                      </TableCell>
                      <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {detailStr || "—"}
                      </TableCell>
                      <TableCell sx={{ color: "#6B7280", fontSize: 12, borderColor: "rgba(255,255,255,0.06)" }}>
                        {new Date(log.createdAt).toLocaleTimeString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
